import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { isCurrentBuild } from "../../../features/release/staleBuild";
import { requestStepFocus } from "../../../components/PageShell";
import { BookingUnauthorized } from "../BookingUnauthorized";
import { useNavigate } from "react-router-dom";
import type { PublicProfessionalProfile } from "../../../features/profile/operations";
import type { FlowSelection } from "../../../features/booking/useFlowState";
import type { HoldState } from "../../../features/booking/useHold";
import type { GuestCheckoutPort } from "../../../features/guestCheckout/port";
import { useGuestCheckout } from "../../../features/guestCheckout/useGuestCheckout";
import { beginHostedCheckout, checkoutBlockFor } from "../../../features/payments/checkoutStart";
import { PAYMENTS_ENABLED } from "../../../config/payments";
import { ensureGuestIdentity, getFirebaseAuth } from "../../../lib/firebase";
import { config } from "../../../config/env";
import { useTranslation } from "../../../i18n/useTranslation";
import StepReview from "../StepReview";
import { reviewStateFor } from "../reviewState";
import { GuestCheckoutView } from "./GuestCheckoutView";
import { guestCodeChunk, payScreensChunk, useChunkBeforeWrite, WithStepChunk } from "../lazySteps";
import { PayChunkFallback } from "../PayChunkFallback";
import { StepSkeleton } from "../StepSkeleton";
import { checkPolicy, type PolicyReread } from "../../../features/profile/policy";
import type { TermsConsent } from "../../../features/terms/useTermsConsent";

/**
 * A port that refuses. Used ONLY so `useFlowGuestCheckout` can be called
 * unconditionally (hooks rule) in builds with no real port, where the guest
 * route is never rendered and nothing can reach these methods.
 */
const UNWIRED: GuestCheckoutPort = {
  requestCode: () => Promise.reject(new Error("guest checkout is not wired")),
  verifyCode: () => Promise.reject(new Error("guest checkout is not wired")),
  submit: () => Promise.reject(new Error("guest checkout is not wired")),
};

/**
 * THE CHECKOUT BELONGS TO THE FLOW, NOT TO THE REVIEW SCREEN. "Check this time
 * again" and "Choose another time" leave Review for the time step; a checkout
 * owned by Review would unmount there and lose its key, which rule 1 says
 * survives a new hold. `Book` stays mounted across steps, so it calls this.
 *
 * It also connects the hold, which is the server's clock and never ours:
 * `expired` is told to the machine, and any DIFFERENT hold id is a new hold
 * (by default a new proof is required: Policy).
 */
export function useFlowGuestCheckout(
  port: GuestCheckoutPort | null,
  hold: HoldState,
  detailsEmail = "",
  onReview = true
) {
  const deps = useMemo(
    () => ({
      port: port ?? UNWIRED,
      ensureIdentity: async () => ({ uid: (await ensureGuestIdentity()).uid }),
      webHost: config.webHost,
      currentBuild: () => isCurrentBuild(),
      // booking-v1 1.20.0: the leave for Stripe (a no-op with the payments gate closed).
      leave: PAYMENTS_ENABLED ? beginHostedCheckout : undefined,
      currentUid: () => getFirebaseAuth().currentUser?.uid ?? null,
    }),
    [port]
  );
  const checkout = useGuestCheckout(deps);
  const { holdExpired, newHold } = checkout;
  /*
   * NO LATCH. `HOLD_EXPIRED` is idempotent, so every `expired` is reported;
   * and ANY different hold id is a new hold, whether the old one expired, was
   * found gone at booking (hold-gone, while useHold still said `held`), or the
   * guest simply picked another time. A new hold means a new proof unless
   * policy says otherwise (NEW_HOLD). An earlier latch waited for `expired`
   * and stranded a hold-gone guest on the hold-ended screen (review A1).
   */
  const heldId = hold.status === "held" ? hold.hold.id : null;
  const lastHeldId = useRef<string | null>(null);
  useEffect(() => {
    if (hold.status === "expired") holdExpired();
  }, [hold.status, holdExpired]);
  useEffect(() => {
    if (!heldId) return;
    const previous = lastHeldId.current;
    lastHeldId.current = heldId;
    if (previous !== null && previous !== heldId) {
      holdExpired();
      newHold();
    }
  }, [heldId, holdExpired, newHold]);
  /*
   * AN EDIT ON THE DETAILS STEP, and only that. The guest may go Back and
   * change the address; nothing verified for the old one may book the new
   * one (review A2). Compared against the last value TYPED ON DETAILS, never
   * against the checkout's own address: the code step's "Change email" moves
   * the checkout to a new address while Details still holds the old one, and
   * comparing those undid the guest's own change (review B1). Held as pending
   * and sent once, with the FINAL value, when the guest is back on Review and
   * no call is in flight. Details updates on every keystroke, so sending on
   * each one dropped the code for an address the guest then typed back
   * (QE A2-1); the address that reaches Review is the one that counts.
   */
  const { detailsChanged } = checkout;
  const typed = detailsEmail.trim();
  const lastTyped = useRef(typed);
  const pending = useRef(false);
  // Bumped when an attempt pause that deferred a Details edit lifts, so the
  // effect below runs again and applies the edit (review A2 on e85c040).
  const [pauseLifted, setPauseLifted] = useState(0);
  const phaseName = checkout.state.phase.name;
  const inFlight = phaseName === "requesting" || phaseName === "verifying" || phaseName === "submitting";
  useEffect(() => {
    // Refs are read and written here, not during render (React Compiler rules).
    if (typed !== lastTyped.current) {
      lastTyped.current = typed;
      pending.current = true;
    }
    if (!pending.current || inFlight || !onReview) return;
    pending.current = false;
    if (!typed) return;
    const deferredUntil = detailsChanged(typed);
    if (deferredUntil === null) return;
    // Paused: keep the edit and try again the moment Backend's time passes.
    pending.current = true;
    const timer = setTimeout(() => setPauseLifted((n) => n + 1), Math.max(0, deferredUntil - Date.now()) + 50);
    return () => clearTimeout(timer);
  }, [typed, inFlight, onReview, detailsChanged, pauseLifted]);

  return checkout;
}

/**
 * THE LIVE ROUTE FOR EVERY ANONYMOUS GUEST.
 *
 * Connects the flow's checkout to what the flow already has: the details the
 * guest typed, the profile's policy, and Review itself.
 *
 * THE ORDER IS PRODUCT'S: Details → Review (with its pre-booking policy) →
 * Code. Review's primary action ASKS FOR A CODE; nothing books until the code
 * step's Confirm (readiness packet Q-P1).
 *
 * Signed-in clients never reach this: the draft contract refuses a guest
 * proof from a signed-in caller, so Book keeps them on the account path.
 */
export function GuestReviewRoute({
  checkout,
  slug,
  profile,
  selection,
  details,
  hold,
  online,
  dropHold,
  terms,
  rereadPolicy,
}: {
  checkout: ReturnType<typeof useGuestCheckout>;
  slug: string;
  profile: PublicProfessionalProfile;
  selection: FlowSelection;
  details: { name: string; email: string };
  hold: HoldState;
  online: boolean;
  /** The flow's `useHold().release`. See `leaveHoldEnded`. */
  dropHold?: () => Promise<void>;
  /** Book's Terms read, shared with the signed-in Review. */
  terms: TermsConsent;
  /** rc.10: read the policy again before Continue or Verify sends anything. */
  rereadPolicy: () => Promise<PolicyReread>;
}) {
  const { t, fmt } = useTranslation();
  const navigate = useNavigate();
  /*
   * A FRESH-LOAD REVIEW WHILE A CHECKOUT MAY STILL BE OPEN (flip checklist §9
   * item 6; design §2.7; the Web Director's Q-A): a live booking checkout
   * (`p`) of this identity for THIS professional blocks a new attempt. The
   * decision is taken once, when this Review mounts, and stays in the entry;
   * the screen (PayBlock) is lazy, in the pay-screens chunk. The gate first,
   * so a closed build folds it away. Booking another professional is never
   * blocked; an /a checkout never blocks (W4 F1).
   */
  const [block, setBlock] = useState(() => (PAYMENTS_ENABLED ? checkoutBlockFor(profile.professionalId) : null));

  const service = profile.services.find((candidate) => candidate.id === selection.serviceId);
  const startTime = hold.status === "held" ? hold.hold.startTime : selection.startTime;
  const timeZone = profile.timezone;
  const summary =
    service && startTime
      ? `${service.name} · ${fmt.date(startTime, timeZone)}, ${fmt.time(startTime, timeZone)}`
      : undefined;
  const when = startTime ? `${fmt.date(startTime, timeZone)}, ${fmt.time(startTime, timeZone)}` : "";
  const backToTime = () => {
    requestStepFocus();
    navigate(`/${slug}/book/time${window.location.search}`);
  };
  /*
   * LEAVING THE HOLD-ENDED SCREEN FORGETS A HOLD THE SERVER HAS ALREADY LOST.
   *
   * Hold-gone at booking means Backend no longer has the hold, but its clock
   * has not run out here, so useHold still reports `held` with the dead id.
   * Time ignores a tap on the already-held slot (it must not manufacture a
   * fresh expiry for a live hold), so "Check this time again" on the same
   * time never asked for a new hold and Continue landed back on "Time ran
   * out" — a loop only "Choose another time" escaped (nightly Prod smoke,
   * qe-guest-cutover-on "a new hold needs a NEW proof"). Dropping it first
   * makes that tap a real re-hold: a new hold id, so a new proof. A genuine
   * expiry is already `expired`, and there is nothing to drop. The release
   * is best effort, and the id it names is one the server has already let go.
   */
  const leaveHoldEnded = () => {
    if (hold.status === "held") void dropHold?.();
    backToTime();
  };
  // The unavailable screen's ways forward (rc.13, the Web Director): the
  // address is edited on Details (a changed one resets the checkout,
  // DETAILS_CHANGED); a signed-in booking does not need guest verification.
  const editEmailOnDetails = () => {
    requestStepFocus();
    navigate(`/${slug}/book/details${window.location.search}`);
  };
  const signInInstead = () => {
    requestStepFocus();
    navigate(`/${slug}/book/signin${window.location.search}`);
  };

  const phase = checkout.state.phase;
  /*
   * rc.10: Continue re-reads the policy before anything is sent. While it
   * reads, the press is busy; a change shows the new policy with the notice
   * (a fresh press is required); an unreadable policy says so and the next
   * press is the retry. A change found at Verify comes back as the checkout's
   * own `policyChanged`.
   */
  const [continueCheck, setContinueCheck] = useState<"checking" | "changed" | "unreadable" | null>(null);
  // The code step is reachable from here: its chunk starts now (rc.12 (a)).
  // Only guest Review: a signed-in client never reaches the code step.
  useEffect(() => {
    guestCodeChunk.prefetch();
  }, []);
  // Continue asks for a code: the code step's chunk must be here first.
  const codeStepReady = useChunkBeforeWrite(guestCodeChunk);
  const onReview = useRef(true);
  useEffect(() => {
    onReview.current = true;
    return () => {
      onReview.current = false;
    };
  }, []);
  /*
   * CONTINUE IS ONE BUSY STATE, FROM THE PRESS UNTIL THE CODE REQUEST'S ANSWER
   * (rc.12, P1, consent integrity; the Web Director). The pipeline is the
   * policy re-read and the code step's chunk, then the code request, which
   * itself first checks the build and the identity. It used to clear its busy
   * state after the re-read, so Continue and the Terms box were ENABLED for
   * that check (about 0.5 s on Dev): an untick there was possible on screen
   * and silently ignored, since the press had already captured "agreed". The
   * press now stays busy (`continueCheck` "checking", which locks the button
   * and the Terms box) until `requestCode` has its answer, and only then
   * settles on what stopped it, if anything.
   */
  const pressing = useRef(false);
  const continueToCode = async (
    decision: NonNullable<ReturnType<TermsConsent["decision"]>>,
    failure: Parameters<typeof reviewStateFor>[0]["commitFailure"]
  ): Promise<"changed" | "unreadable" | null> => {
    // The policy this Review shows, at the press, read again first (rc.10).
    const shown = profile.cancellationPolicyDisplay;
    // The code step's chunk, alongside the read (rc.12 (a)): a prefetched
    // one adds no wait; a dead one sends nothing (useChunkBeforeWrite).
    const [check, codeStepHere] = await Promise.all([checkPolicy(rereadPolicy, shown), codeStepReady()]);
    // Review left during the read (Back): send nothing. A hold that lapses
    // meanwhile keeps this route mounted (hold-ended renders inside it);
    // a code may then still be asked for, and nothing can be booked.
    if (!onReview.current || !codeStepHere) return null;
    if (check !== "same") return check;
    if (failure) {
      // A DEFINITE failure (the server said no appointment was made)
      // leaves nothing to reach: a fresh checkout, and "Try again" asks
      // for a code at once rather than landing on Continue (review A3).
      // The review's own state decides whether the action is offered.
      checkout.restart();
      if (hold.status === "held") await checkout.requestCode(details.email, decision, shown);
      return null;
    }
    // After a Terms refusal with the proof still held: record the new
    // decision and go to the verified code screen, whose Confirm books
    // under the SAME key and proof. No new code, and nothing sent here.
    // Or after a policy change found at Verify, with the code challenge
    // still held (Q2): back to the code step, no new email.
    if ((phase.name === "ready" || (phase.name === "code" && checkout.state.policyChanged)) && checkout.state.terms === null) {
      checkout.acceptTerms(decision, shown);
      return null;
    }
    if (hold.status !== "held") return null;
    // Awaited: the busy state holds until the request has its answer.
    await checkout.requestCode(details.email, decision, shown);
    return null;
  };
  const policyNote = continueCheck === "changed" || continueCheck === "unreadable" ? continueCheck : checkout.state.policyChanged ? "changed" : null;
  // A Terms refusal is shown on Review, as the signed-in path shows it: the
  // notice (stale, required) or frame 4.5 (unavailable) above Terms re-read.
  const termsRefusal = checkout.state.termsRefusal;
  const termsFailure: Parameters<typeof reviewStateFor>[0]["commitFailure"] = termsRefusal
    ? { kind: "terms", refusal: termsRefusal.refusal }
    : null;
  const review = (notice: ReactNode, failure: Parameters<typeof reviewStateFor>[0]["commitFailure"] = null) => (
    <StepReview
      verifiedGuest
      slug={slug}
      profile={profile}
      selection={selection}
      client={details}
      state={reviewStateFor({
        online,
        booking: failure || termsFailure ? "failed" : phase.name === "requesting" ? "sending" : "idle",
        commitFailure: failure ?? termsFailure,
        hold,
        afterCheck: !failure && termsRefusal?.afterCheck === true,
      })}
      secondsLeft={hold.status === "held" ? hold.secondsLeft : null}
      holdExpiresAt={hold.status === "held" ? hold.hold.expiresAt : null}
      sending={phase.name === "requesting" || continueCheck === "checking"}
      policyNote={policyNote}
      cancellationPolicyDisplay={profile.cancellationPolicyDisplay}
      terms={terms}
      notice={notice}
      primaryLabels={{ idle: t("guest.review.continue"), busy: t("guest.code.sending") }}
      onConfirm={() => {
        // THE TERMS ARE DECIDED BEFORE ANY CODE IS SENT. Continue is disabled
        // until they are; this refuses anyway, because the handler must not
        // depend on the button (Book.guards.test.tsx).
        const decision = terms.decision();
        // ONE PRESS AT A TIME, decided synchronously (the Web Director): a
        // second press starts neither a second re-read nor a second request.
        // KEPT KNOWINGLY AS DEFENCE IN DEPTH, AND RECORDED AS SUCH: measured,
        // React 19 commits a click's busy state before the next click (even
        // two clicks in one task), so StepReview's own `sending` guard already
        // stops the second press, and deleting this latch fails nothing
        // (busy-continuous-rc12.spec.js, "two presses in ONE task", green
        // without it). It holds for any caller not wired through that button.
        if (!decision || pressing.current) return;
        pressing.current = true;
        // A user's step advance: the next screen's heading takes focus (c6-3).
        requestStepFocus();
        setContinueCheck("checking");
        void continueToCode(decision, failure).then(
          (stop) => setContinueCheck(stop),
          () => setContinueCheck(null)
        ).finally(() => {
          pressing.current = false;
        });
      }}
    />
  );

  // Only before this person's own attempt starts: once they act on this
  // Review, the screen is theirs.
  if (PAYMENTS_ENABLED && payScreensChunk && block?.blocked && phase.name === "entry") {
    const checkoutRef = block.ref;
    return (
      <WithStepChunk chunk={payScreensChunk} fallback={<StepSkeleton testId="step-chunk-loading" />} failed={<PayChunkFallback />}>
        {({ PayBlock }) => (
          <PayBlock
            checkoutRef={checkoutRef}
            identity={block.identity}
            professionalId={profile.professionalId}
            onLift={() => setBlock(null)}
            onRecheck={() => setBlock(checkoutBlockFor(profile.professionalId))}
            onClosed={dropHold}
          />
        )}
      </WithStepChunk>
    );
  }

  return (
    <GuestCheckoutView
      checkout={checkout}
      hold={{ expiresAt: hold.status === "held" ? hold.hold.expiresAt : null, timeZone, summary }}
      submit={{
        holdId: hold.status === "held" ? hold.hold.id : null,
        // The server's expiry, as received: what a later replay is judged against (rc.11 1c).
        holdExpiresAt: hold.status === "held" ? hold.hold.expiresAt : null,
        fullName: details.name.trim(),
        expected: {
          professionalId: profile.professionalId,
          serviceId: selection.serviceId ?? "",
          startTime: startTime ?? "",
        },
      }}
      business={profile.businessName}
      when={when}
      name={details.name}
      review={(notice) => review(notice)}
      rereadPolicy={rereadPolicy}
      failed={review(null, phase.name === "booking-failed" ? phase.failure : null)}
      unauthorized={<BookingUnauthorized business={profile.businessName} />}
      onRecheckTime={leaveHoldEnded}
      onChooseAnother={leaveHoldEnded}
      onChangeEmailOnDetails={editEmailOnDetails}
      onSignIn={signInInstead}
    />
  );
}
